/**
 * useSettingsData — data loading, retry logic, and billing handlers for Settings page.
 *
 * Extracted from Settings.js to reduce component size.
 * Owns all async state (user, usage, invoices, plans, packages)
 * and all billing action handlers (upgrade, portal, buy credits).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useToast } from "../components/Toast";
import { useCredits } from "./useCredits";
import { accountApi, analyticsApi, billingApi } from "../services/api";
import errorTracker from "../utils/errorTracker";
import { getErrorMessage } from "../utils/formatters";
import { RETRY } from "../config/constants";
import { format, subDays, subMonths, startOfMonth, endOfMonth } from "date-fns";
import {
  getBackoffDelay,
  categorizeError,
  getDetailedErrorMessage,
} from "./useSettingsRetry";

const DEFAULT_SALES_EMAIL = "sales@kwibl.io";
const BILLING_PARAMS_PROCESSED_KEY = "settings_billing_params_processed";

export function useSettingsData() {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [user, setUser] = useState(null);
  const [usageSummary, setUsageSummary] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [upgradingPlan, setUpgradingPlan] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [salesEmail, setSalesEmail] = useState(DEFAULT_SALES_EMAIL);
  const [plans, setPlans] = useState(null);
  const [planOrder, setPlanOrder] = useState(null);
  const [confirmingUpgrade, setConfirmingUpgrade] = useState(null);
  const [creditPackages, setCreditPackages] = useState(null);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [purchasingPackage, setPurchasingPackage] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [errorCategory, setErrorCategory] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastRetryTime, setLastRetryTime] = useState(0);
  const [debounceRemaining, setDebounceRemaining] = useState(0);
  const retryControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const {
    credits,
    thresholds,
    usageThisMonth,
    loading: creditsLoading,
    refresh: refreshCredits,
  } = useCredits();

  // Derived values
  const currentPlanId = user?.plan_id || "free";
  const planStatus = user?.plan_status || "active";
  const planExpiresAt = user?.plan_expires_at;
  const currentPlan = plans?.find((p) => p.id === currentPlanId);
  // undefined = plans not yet loaded; object = loaded (null values = unlimited)
  const monthlyAllocations =
    plans == null || !currentPlan ? undefined : currentPlan.monthlyCredits;

  // ── Data loaders ─────────────────────────────────────────────────────

  const loadUser = useCallback(async (signal, isRetry = false) => {
    setLoadingUser(true);
    if (!isRetry) {
      setLoadError(null);
      setErrorCategory(null);
    }
    try {
      const response = await accountApi.getCurrentUser({ signal });
      setUser(response.user);
      setLoadError(null);
      setErrorCategory(null);
      setRetryCount(0);
    } catch (err) {
      if (
        err?.name === "AbortError" ||
        err?.name === "CanceledError" ||
        err?.code === "ERR_CANCELED"
      )
        return;
      const category = categorizeError(err);
      setErrorCategory(category);
      if (category !== "unknown") {
        setLoadError(
          getDetailedErrorMessage(err, "Failed to load user profile"),
        );
        return;
      }
      errorTracker.captureException(err, { context: "loadUser" });
      setLoadError(getDetailedErrorMessage(err, "Failed to load user profile"));
    } finally {
      setLoadingUser(false);
    }
  }, []);

  const loadUsageSummary = useCallback(async (signal) => {
    setLoadingUsage(true);
    try {
      const now = new Date();
      const endDate = format(now, "yyyy-MM-dd");
      const startDate = format(subDays(now, 30), "yyyy-MM-dd");
      const response = await analyticsApi.getUsageSummary(startDate, endDate, {
        signal,
      });
      setUsageSummary(response);
    } catch (err) {
      if (
        err?.name === "AbortError" ||
        err?.name === "CanceledError" ||
        err?.code === "ERR_CANCELED"
      )
        return;
      errorTracker.captureException(err, { context: "loadUsageSummary" });
      toastRef.current.error(
        getErrorMessage(err, "Failed to load usage summary"),
      );
    } finally {
      setLoadingUsage(false);
    }
  }, []);

  const loadInvoiceHistory = useCallback(async (signal) => {
    setLoadingInvoices(true);
    try {
      const now = new Date();
      const months = [];
      for (let i = 1; i <= 3; i++) {
        const monthDate = subMonths(now, i);
        const start = format(startOfMonth(monthDate), "yyyy-MM-dd");
        const end = format(endOfMonth(monthDate), "yyyy-MM-dd");
        months.push({ monthDate, start, end });
      }

      let aborted = false;
      const results = await Promise.all(
        months.map(({ start, end }) =>
          analyticsApi.getUsageSummary(start, end, { signal }).catch((err) => {
            if (
              err == null ||
              err?.name === "AbortError" ||
              err?.name === "CanceledError" ||
              err?.code === "ERR_CANCELED"
            ) {
              aborted = true;
            } else {
              errorTracker.captureException(err, {
                context: "loadInvoiceHistory",
                period: `${start} to ${end}`,
              });
            }
            return null;
          }),
        ),
      );

      if (!aborted && results.every((r) => r === null)) {
        toastRef.current.error(
          getErrorMessage(null, "Failed to load usage history"),
        );
      }

      const history = months
        .map(({ monthDate }, idx) => {
          const data = results[idx];
          if (!data?.summary) return null;
          return {
            month: format(monthDate, "MMMM yyyy"),
            messages: data.summary.totalMessages || 0,
            tokens: data.summary.totalTokens || 0,
            cost: data.summary.totalCost || 0,
          };
        })
        .filter(Boolean);

      setInvoiceHistory(history);
    } catch (err) {
      if (
        err == null ||
        err?.name === "AbortError" ||
        err?.name === "CanceledError" ||
        err?.code === "ERR_CANCELED"
      )
        return;
      errorTracker.captureException(err, { context: "loadInvoiceHistory" });
      toastRef.current.error(
        getErrorMessage(err, "Failed to load usage history"),
      );
    } finally {
      setLoadingInvoices(false);
    }
  }, []);

  // ── Initial data loading ─────────────────────────────────────────────

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const controller = new AbortController();
    loadUser(controller.signal);
    loadUsageSummary(controller.signal);
    loadInvoiceHistory(controller.signal);

    billingApi
      .getPlans({ signal: controller.signal })
      .then((response) => {
        if (response?.salesEmail) setSalesEmail(response.salesEmail);
        if (response?.plans) setPlans(response.plans);
        if (response?.planOrder) setPlanOrder(response.planOrder);
      })
      .catch((err) => {
        if (
          err?.name === "AbortError" ||
          err?.name === "CanceledError" ||
          err?.code === "ERR_CANCELED"
        )
          return;
        console.warn(
          "Failed to fetch plans from API, using fallback plan data",
          err,
        );
      });

    billingApi
      .getCreditPackages({ signal: controller.signal })
      .then((response) => {
        if (response?.packages) setCreditPackages(response.packages);
      })
      .catch((err) => {
        if (
          err?.name === "AbortError" ||
          err?.name === "CanceledError" ||
          err?.code === "ERR_CANCELED"
        )
          return;
        // [] not null — lets component distinguish "empty" from "still loading"
        console.warn("Failed to fetch credit packages", err);
        setCreditPackages([]);
      })
      .finally(() => setLoadingPackages(false));

    return () => controller.abort();
  }, [loadInvoiceHistory, loadUsageSummary, loadUser]);

  // ── Checkout/portal URL param handling ────────────────────────────────

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutResult = params.get("checkout");
    const portalReturn = params.get("portal");

    if (!checkoutResult && !portalReturn) return;

    const paramsKey = `${checkoutResult || ""}_${portalReturn || ""}`;
    const alreadyProcessed = sessionStorage.getItem(
      BILLING_PARAMS_PROCESSED_KEY,
    );

    if (alreadyProcessed === paramsKey) {
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }

    sessionStorage.setItem(BILLING_PARAMS_PROCESSED_KEY, paramsKey);
    window.history.replaceState({}, "", window.location.pathname);

    let creditRefreshTimer1, creditRefreshTimer2;
    if (checkoutResult === "credit_success") {
      toast.success("Credits purchased successfully!");
      creditRefreshTimer1 = setTimeout(() => refreshCredits(), 1500);
      creditRefreshTimer2 = setTimeout(() => refreshCredits(), 5000);
    } else if (checkoutResult === "credit_canceled") {
      toast.info("Credit purchase canceled.");
    } else if (checkoutResult === "success") {
      toast.success("Subscription updated successfully!");
    } else if (checkoutResult === "canceled") {
      toast.info("No changes made to your plan.");
    }

    const refreshController = new AbortController();
    loadUser(refreshController.signal);

    const cleanupTimeout = setTimeout(() => {
      sessionStorage.removeItem(BILLING_PARAMS_PROCESSED_KEY);
    }, 5000);

    return () => {
      refreshController.abort();
      clearTimeout(cleanupTimeout);
      clearTimeout(creditRefreshTimer1);
      clearTimeout(creditRefreshTimer2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Billing action handlers ──────────────────────────────────────────

  const handleUpgradeClick = (plan) => setConfirmingUpgrade(plan);
  const handleUpgradeCancel = () => setConfirmingUpgrade(null);

  const handleUpgradeConfirm = async () => {
    if (!confirmingUpgrade) return;
    const planId = confirmingUpgrade.id;
    setConfirmingUpgrade(null);
    setUpgradingPlan(planId);
    try {
      const baseUrl = window.location.origin + window.location.pathname;
      const { url } = await billingApi.createCheckoutSession(
        planId,
        `${baseUrl}?checkout=success`,
        `${baseUrl}?checkout=canceled`,
      );
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Failed to create checkout session");
        setUpgradingPlan(null);
      }
    } catch (err) {
      errorTracker.captureException(err, { context: "handleUpgrade", planId });
      toast.error(getErrorMessage(err, "Failed to start upgrade process"));
      setUpgradingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    setOpeningPortal(true);
    try {
      const baseUrl = window.location.origin + window.location.pathname;
      const { url } = await billingApi.createPortalSession(
        `${baseUrl}?portal=return`,
      );
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Failed to open billing portal");
        setOpeningPortal(false);
      }
    } catch (err) {
      errorTracker.captureException(err, {
        context: "handleManageSubscription",
      });
      toast.error(getErrorMessage(err, "Failed to open billing portal"));
      setOpeningPortal(false);
    }
  };

  const handleBuyCredits = async (pkg) => {
    setPurchasingPackage(pkg.id);
    try {
      const baseUrl = window.location.origin + window.location.pathname;
      const { url } = await billingApi.createCreditCheckout(
        pkg.id,
        `${baseUrl}?checkout=credit_success`,
        `${baseUrl}?checkout=credit_canceled`,
      );
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Failed to create credit checkout session");
        setPurchasingPackage(null);
      }
    } catch (err) {
      errorTracker.captureException(err, {
        context: "handleBuyCredits",
        packageId: pkg.id,
      });
      toast.error(getErrorMessage(err, "Failed to start credit purchase"));
      setPurchasingPackage(null);
    }
  };

  // ── Retry handler ────────────────────────────────────────────────────

  const handleRetry = useCallback(() => {
    const now = Date.now();
    const timeSinceLastRetry = now - lastRetryTime;
    if (timeSinceLastRetry < RETRY.DEBOUNCE_MS) {
      const remaining = Math.ceil(
        (RETRY.DEBOUNCE_MS - timeSinceLastRetry) / 1000,
      );
      setDebounceRemaining(remaining);

      // Countdown timer so user sees "Wait 3s… 2s… 1s…"
      if (debounceTimerRef.current) clearInterval(debounceTimerRef.current);
      debounceTimerRef.current = setInterval(() => {
        setDebounceRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(debounceTimerRef.current);
            debounceTimerRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return;
    }

    if (retryControllerRef.current) retryControllerRef.current.abort();
    if (debounceTimerRef.current) {
      clearInterval(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const newRetryCount = retryCount + 1;
    setRetryCount(newRetryCount);
    setLoadError(null);
    setErrorCategory(null);
    setIsRetrying(true);
    setLastRetryTime(now);
    setDebounceRemaining(0);

    const controller = new AbortController();
    retryControllerRef.current = controller;

    const backoffDelay =
      newRetryCount > 1 ? getBackoffDelay(newRetryCount - 1) : 0;

    const executeRetry = () => {
      Promise.all([
        loadUser(controller.signal, true),
        loadUsageSummary(controller.signal),
        loadInvoiceHistory(controller.signal),
      ]).finally(() => {
        setIsRetrying(false);
        retryControllerRef.current = null;
      });
    };

    if (backoffDelay > 0) {
      setTimeout(executeRetry, backoffDelay);
    } else {
      executeRetry();
    }
  }, [
    loadUser,
    loadUsageSummary,
    loadInvoiceHistory,
    lastRetryTime,
    retryCount,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (retryControllerRef.current) retryControllerRef.current.abort();
      if (debounceTimerRef.current) clearInterval(debounceTimerRef.current);
    };
  }, []);

  return {
    // Data
    user,
    usageSummary,
    invoiceHistory,
    plans,
    planOrder,
    creditPackages,
    salesEmail,
    credits,
    thresholds,
    usageThisMonth,
    monthlyAllocations,
    currentPlanId,
    planStatus,
    planExpiresAt,

    // Loading states
    loadingUser,
    loadingUsage,
    loadingInvoices,
    loadingPackages,
    creditsLoading,
    isRetrying,

    // Error states
    loadError,
    errorCategory,
    retryCount,
    debounceRemaining,

    // Billing action states
    upgradingPlan,
    openingPortal,
    confirmingUpgrade,
    purchasingPackage,

    // Handlers
    handleUpgradeClick,
    handleUpgradeConfirm,
    handleUpgradeCancel,
    handleManageSubscription,
    handleBuyCredits,
    handleRetry,
    refreshCredits,
    toast,
  };
}
